import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Loader2, Images, Wand2, ArrowRight } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { api, imgUrl, track, SITE_URL } from "@/lib/api";

const OBJ_LABELS = { dom: "Rodinný dom", bytovka: "Bytový dom", budova: "Budova" };
const META_TITLE = "Najlepšia vizualizácia domu a fasády — galéria AI návrhov | Zateplenia.eu";
const META_DESC = "Galéria reálnych AI vizualizácií zateplených fasád: pred a po, typ objektu, zvolené farby a orientačná cena zateplenia. Inšpirujte sa a vytvorte si vlastnú vizualizáciu zadarmo.";

const setMeta = (name, content) => {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setMetaProp = (property, content) => {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute("property", property); document.head.appendChild(el); }
  el.setAttribute("content", content);
};
const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
};

export const GalleryCard = ({ v, index }) => {
  const [variant, setVariant] = useState(0);
  const imgs = v.images || [];
  const after = imgs.length ? imgs[Math.min(variant, imgs.length - 1)] : "";
  const colors = v.colors || v.palette || "";
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: (index % 3) * 0.08 }}
      className="border border-white/10 bg-ink-2 overflow-hidden flex flex-col" data-testid={`gallery-card-${index}`}>
      {v.original && after ? (
        <BeforeAfterSlider before={imgUrl(v.original)} after={imgUrl(after)} testid={`gallery-slider-${index}`} afterLabel={`Návrh ${variant + 1}`} />
      ) : after ? (
        <img src={imgUrl(after)} alt={`Vizualizácia zateplenia — ${OBJ_LABELS[v.object_type] || "objekt"}`} className="w-full aspect-[4/3] object-cover" loading="lazy" />
      ) : null}
      <div className="p-4 flex flex-col gap-2 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-gold font-mono2 text-xs uppercase tracking-widest">{OBJ_LABELS[v.object_type] || "Objekt"}</span>
          {imgs.length > 1 && (
            <div className="flex gap-1" data-testid={`gallery-variants-${index}`}>
              {imgs.map((_, i) => (
                <button key={i} onClick={() => setVariant(i)} data-testid={`gallery-variant-${index}-${i}`}
                  aria-label={`Návrh ${i + 1}`}
                  className={`px-2 py-0.5 text-[11px] border transition-colors ${i === variant ? "border-gold text-gold bg-gold/10" : "border-white/15 text-white/50 hover:border-white/40"}`}>
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>
        {colors && <p className="text-white/60 text-sm">Farby: {colors}</p>}
        {v.estimate && (
          <p className="text-white/80 text-sm mt-auto pt-2 border-t border-white/5">
            Orientačná cena zateplenia: <span className="text-gold font-bold">od {Math.round(v.estimate.typical_total).toLocaleString("sk-SK")} €</span>
            <span className="text-white/40 text-xs block mt-0.5">typický {v.estimate.object_label} (~{v.estimate.typical_area} m²) · {Math.round(v.estimate.price_per_m2)} €/m² s DPH</span>
          </p>
        )}
      </div>
    </motion.article>
  );
};

export default function Galeria() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    document.title = META_TITLE;
    setMeta("description", META_DESC);
    setCanonical(`${SITE_URL}/galeria`);
    setMetaProp("og:title", META_TITLE);
    setMetaProp("og:description", META_DESC);
    setMetaProp("og:type", "website");
    setMetaProp("og:url", `${SITE_URL}/galeria`);
    track("visit", "galeria");
    api.get("/gallery").then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!items.length) return;
    const firstImg = items.find((v) => (v.images || []).length)?.images?.[0];
    if (firstImg) setMetaProp("og:image", imgUrl(firstImg));
    const ld = {
      "@context": "https://schema.org",
      "@type": "ImageGallery",
      "name": "Najlepšia vizualizácia domu a fasády — Zateplenia.eu",
      "description": META_DESC,
      "url": `${SITE_URL}/galeria`,
      "inLanguage": "sk-SK",
      "image": items.flatMap((v) => (v.images || []).map((u, i) => ({
        "@type": "ImageObject",
        "contentUrl": imgUrl(u),
        "name": `Vizualizácia zateplenia — ${OBJ_LABELS[v.object_type] || "objekt"}, návrh ${i + 1}`,
      }))),
    };
    let s = document.getElementById("ld-json");
    if (!s) { s = document.createElement("script"); s.id = "ld-json"; s.type = "application/ld+json"; document.head.appendChild(s); }
    s.textContent = JSON.stringify(ld);
  }, [items]);

  const list = items.filter((v) => !filter || v.object_type === filter);

  return (
    <div className="bg-ink min-h-screen" data-testid="gallery-page">
      <Header />

      <section className="pt-36 pb-12">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <div className="inline-flex items-center gap-2 text-gold font-mono2 uppercase tracking-[0.3em] text-xs mb-6">
            <Images className="w-4 h-4" /> Galéria · AI vizualizácie
          </div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-white leading-[0.95] text-4xl sm:text-5xl lg:text-6xl max-w-4xl" data-testid="gallery-title">
            Najlepšia vizualizácia<br /><span className="text-gold">domu a fasády</span>
          </motion.h1>
          <p className="text-white/70 text-base md:text-lg max-w-2xl mt-6">
            Reálne návrhy zateplených fasád vytvorené našou AI pre zákazníkov, ktorí súhlasili so zverejnením.
            Potiahnite posuvník a porovnajte stav <strong className="text-white">pred a po</strong> — ku každej vizualizácii
            uvádzame typ objektu, zvolené farby a orientačnú cenu zateplenia.
          </p>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10">
          <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="gallery-filters">
            {[["", "Všetky"], ["dom", "Rodinné domy"], ["bytovka", "Bytové domy"], ["budova", "Budovy"]].map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`gallery-filter-${k || "all"}`}
                className={`px-4 py-2 text-xs font-mono2 uppercase tracking-widest border transition-colors ${filter === k ? "border-gold bg-gold text-ink" : "border-white/15 text-white/60 hover:border-gold/50"}`}>
                {l}
              </button>
            ))}
            <span className="text-white/40 text-sm ml-2" data-testid="gallery-count">{list.length} vizualizácií</span>
          </div>

          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
          ) : list.length ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
              {list.map((v, i) => <GalleryCard key={v.id} v={v} index={i} />)}
            </div>
          ) : (
            <div className="border border-white/10 bg-ink-2 p-12 text-center" data-testid="gallery-empty">
              <Images className="w-10 h-10 text-white/20 mx-auto mb-4" />
              <p className="text-white/60 mb-2">Galériu práve pripravujeme — prvé vizualizácie čakajú na schválenie.</p>
              <p className="text-white/40 text-sm">Zatiaľ si môžete <Link to="/vizualizacia" className="text-gold hover:underline">vytvoriť vlastnú vizualizáciu zadarmo</Link>.</p>
            </div>
          )}

          <div className="mt-14 border border-gold/40 bg-gold/5 p-6 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h2 className="font-display text-2xl md:text-3xl text-white">Chcete vidieť vlastnú fasádu?</h2>
              <p className="text-white/60 text-sm md:text-base mt-2 max-w-xl">
                Nahrajte fotku domu alebo bytovky, vyberte farby a AI vám vytvorí 3 realistické návrhy — bezplatne a nezáväzne.
              </p>
            </div>
            <Link to="/vizualizacia" data-testid="gallery-cta"
              className="shrink-0 bg-gold text-ink px-8 py-4 font-bold uppercase tracking-wider hover:bg-gold-light transition-colors flex items-center gap-2">
              <Wand2 className="w-4 h-4" /> Vytvoriť vizualizáciu <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/#kalkulacka" data-testid="gallery-cta-calc"
              className="shrink-0 border border-gold/50 text-gold px-8 py-4 font-bold uppercase tracking-wider hover:bg-gold hover:text-ink transition-colors">
              Vypočítať cenu
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
